import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { TextSearchPanelData } from './useTextSearchPanel';
import { LoadingIndicator } from '../LoadingIndicator';

export interface GenericTextSearchPanelProps<T> {
  search: TextSearchPanelData<T>;
  
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => React.ReactNode;
  
  onItemSelect: (item: T) => void;

  placeholderText?: string;
  searchingText?: React.ReactNode;
  noResultsText?: React.ReactNode;
}

export const GenericTextSearchPanel = <T,>(props: GenericTextSearchPanelProps<T>) => {
  const { 
    search, 
    getItemId,
    getItemLabel,
    onItemSelect,
    placeholderText = "Search...",
    searchingText = "Searching...",
    noResultsText = "No results found."
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  // Автофокус при открытии
  useEffect(() => {
    if (search.isActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [search.isActive]);

  if (!search.isActive) return null;

  const results = search.results.data || [];
  const hasResults = search.filter.trim().length > 0 && results.length > 0;
  const isSearching = search.isSearching;

  const handleNavigate = (item: T) => {
    search.setIsActive(false);
    search.clearFilter();
    onItemSelect(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (hasResults) {
        // Навигация по первому результату
        handleNavigate(results[0]);
      }
    } else if (e.key === 'Escape') {
      search.setIsActive(false);
    }
  };

  return (
    <Overlay onClick={() => search.setIsActive(false)}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder={placeholderText}
          value={search.filter}
          onChange={(e) => search.setFilter(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        
        {isSearching && (
          <Menu>
            <StatusText>
              {searchingText}
              <LoadingIndicator sizeVariant="s" />
            </StatusText>
          </Menu>
        )}

        {!isSearching && hasResults && (
          <Menu>
            {results.map((item) => (
              <MenuItem key={getItemId(item)} onClick={() => handleNavigate(item)}>
                {getItemLabel(item)}
              </MenuItem>
            ))}
          </Menu>
        )}

        {search.filter.trim().length > 0 && results.length === 0 && !isSearching && (
          <Menu>
            <StatusText>{noResultsText}</StatusText>
          </Menu>
        )}
      </Panel>
    </Overlay>
  );
};

// --- Styles ---

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 10vh;
`;

const Panel = styled.div`
  width: 100%;
  max-width: 600px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  font-size: 1.2rem;
  border: none;
  outline: none;
  background: transparent;
  color: #333333;
  border-bottom: 1px solid #eeeeee;

  &::placeholder {
    color: #999999;
  }
`;

const Menu = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.div`
  padding: 12px 20px;
  cursor: pointer;
  font-size: 1rem;
  color: #333333;
  border-left: 3px solid transparent;
  transition: all 0.15s ease-in-out;

  &:hover {
    background-color: #eeeeee;
    border-left-color: #dddddd;
  }
`;

const StatusText = styled.div`
  padding: 16px 20px;
  color: #888888;
  font-style: italic;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;